import { useCallback, useState } from "react";

import { useAction } from "../lib/useAction";
import { Alert } from "./Alert";
import { Button } from "./Button";
import { Field, Input } from "./Field";
import { Modal } from "./Modal";

/**
 * 확인 창 — `window.confirm` / `window.prompt` 대체.
 *
 * 브라우저 기본 창은 버튼 문구가 "확인/취소" 로 고정이라 "회수" 처럼 결과를 말하는 라벨을 쓸 수
 * 없고, 응답을 기다리는 동안 진행 상태도 보여줄 수 없습니다. 이 창은
 *  - 확인 버튼이 **동작 이름**을 씁니다(`confirmLabel`).
 *  - 확인을 누르면 응답까지 `pending` 이고, 그동안 취소·Escape·배경 클릭으로 닫히지 않습니다 —
 *    요청이 이미 나갔는데 창만 사라지면 결과를 알 수 없습니다.
 *  - 실패하면 창을 유지하고 오류를 **창 안에** 보여줍니다(다시 시도할 수 있게). 성공하면 닫습니다.
 *  - `reason` 을 주면 선택 입력칸을 하나 둡니다(회수 사유 등).
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  tone = "danger",
  reason,
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  tone?: "danger" | "primary";
  reason?: { label: string; placeholder?: string };
  /** 실제 동작. resolve 되면 창이 닫히고, reject 되면 메시지를 창 안에 보여줍니다. */
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const action = useAction();
  const [text, setText] = useState("");
  const { pending } = action;

  const close = useCallback(() => {
    if (!pending) onClose();
  }, [pending, onClose]);

  const confirm = async () => {
    if (await action.run(() => onConfirm(text.trim()))) onClose();
  };

  return (
    <Modal
      title={title}
      sub={message}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={pending} onClick={close}>
            취소
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            pending={pending}
            onClick={() => void confirm()}
          >
            {confirmLabel}
          </Button>
        </>
      }
      status={action.error ? <Alert kind="error">{action.error}</Alert> : null}
    >
      {reason ? (
        <Field id="confirm-reason" label={reason.label}>
          <Input
            id="confirm-reason"
            placeholder={reason.placeholder}
            value={text}
            disabled={pending}
            onChange={(ev) => setText(ev.target.value)}
          />
        </Field>
      ) : null}
    </Modal>
  );
}
